// Utility functions for rendering tutorial content

import React from 'react';
import { Banner, BodyText, Heading } from '@knowtex-ai/components';
import { STEP_IDS } from './tooltipConstants';

/**
 * Splits authored copy on newlines into line breaks
 * @param {string} text - Raw content text
 * @returns {Array<JSX.Element>} - Text with <br /> between lines
 */
const withLineBreaks = (text) => {
  const lines = (text || '').split('\n');

  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

/**
 * Block spacing between content items
 * @param {string} stepId - Current step ID
 * @returns {string} - Margin class
 */
const blockMargin = (stepId) => (stepId === STEP_IDS.WELCOME_MODAL ? '' : 'mb-2');

/**
 * Renders a paragraph content item
 * @param {object} item - Content item with text
 * @param {number} index - Item index
 * @param {string} stepId - Current step ID
 * @returns {JSX.Element} - Rendered paragraph
 */
const renderParagraph = (item, index, stepId) => {
  // The closing step writes its section headings as plain paragraphs.
  const headingTexts = [
    'Your Data is Protected',
    'Data Protection:',
    'Automatic Data Deletion:',
    'Privacy-First Design:'
  ];

  const text = item?.text || '';
  const isHeading = stepId === 'tutorial_complete' && headingTexts.includes(text.trim());

  if (isHeading) {
    return (
      <Heading key={index} as="h3" size="x-small" className={blockMargin(stepId)}>
        {withLineBreaks(text)}
      </Heading>
    );
  }

  return (
    <BodyText key={index} className={blockMargin(stepId)}>
      {withLineBreaks(text)}
    </BodyText>
  );
};

/**
 * Renders a list content item
 * @param {object} item - Content item with items array
 * @param {number} index - Item index
 * @param {string} stepId - Current step ID
 * @returns {JSX.Element} - Rendered list
 */
const renderList = (item, index, stepId) => {
  const items = item?.items || [];

  return (
    <ul
      key={index}
      className={`list-disc list-inside text-left ${blockMargin(stepId)}`}
    >
      {items.map((listItem, i) => (
        <li key={i} className="mb-1">
          <BodyText as="span">{listItem}</BodyText>
        </li>
      ))}
    </ul>
  );
};

/**
 * Renders a callout content item — important, tip, note, highlight, callout
 * and action all render the same way, differing only in urgency
 * @param {object} item - Content item with text
 * @param {number} index - Item index
 * @param {string} stepId - Current step ID
 * @param {string} variant - Banner variant
 * @returns {JSX.Element} - Rendered callout
 */
const renderCallout = (item, index, stepId, variant = 'info') => {
  return (
    <Banner key={index} variant={variant} className={blockMargin(stepId)}>
      {withLineBreaks(item?.text || '')}
    </Banner>
  );
};

/**
 * Renders a video content item
 * @param {object} item - Content item with video sources
 * @param {number} index - Item index
 * @returns {JSX.Element} - Rendered video
 */
const renderVideo = (item, index) => {
  return (
    <div key={index} className="rounded-xl overflow-hidden bg-black mb-2">
      {/* Cap the height (inline so it can't be dropped by CSS purging) so the
          card — header + copy + video + footer — stays within the viewport and
          never pushes the Next button off-screen. */}
      <video
        controls
        className="block w-full object-contain mx-auto"
        style={{ maxHeight: '35vh' }}
      >

        {item?.srcMp4 && <source src={item.srcMp4} type="video/mp4" />}
        {item?.srcWebm && <source src={item.srcWebm} type="video/webm" />}
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

/**
 * Renders a script content item
 * @param {object} item - Content item with title and items, or just text
 * @param {number} index - Item index
 * @param {string} stepId - Current step ID
 * @returns {JSX.Element} - Rendered script
 */
const renderScript = (item, index, stepId) => {
  const title = item?.title || '';
  const items = item?.items || [];
  const text = item?.text || '';

  if (items.length === 0) {
    return (
      <Banner key={index} variant="info" heading={title || undefined} className={blockMargin(stepId)}>
        {withLineBreaks(text)}
      </Banner>
    );
  }

  return (
    <Banner key={index} variant="info" heading={title || undefined} className={blockMargin(stepId)}>
      <ul className="list-disc list-inside">
        {items.map((scriptItem, i) => (
          <li key={i} className="mb-1">{scriptItem}</li>
        ))}
      </ul>
    </Banner>
  );
};

/**
 * Renders an example content item
 * @param {object} item - Content item with items array
 * @param {number} index - Item index
 * @param {string} stepId - Current step ID
 * @returns {JSX.Element} - Rendered example
 */
const renderExample = (item, index, stepId) => {
  const items = item?.items || [];

  return (
    <Banner key={index} variant="info" heading="Example" className={blockMargin(stepId)}>
      {items.map((ex, i) => (
        <div key={i} className="mb-1">
          <span className="font-medium">{ex?.label || ''}:</span>{' '}
          <span>{ex?.value || ''}</span>
        </div>
      ))}
    </Banner>
  );
};

/**
 * Renders an info content item
 * @param {object} item - Content item with title and text
 * @param {number} index - Item index
 * @param {string} stepId - Current step ID
 * @returns {JSX.Element} - Rendered info
 */
const renderInfo = (item, index, stepId) => {
  return (
    <Banner
      key={index}
      variant="info"
      heading={item?.title || undefined}
      className={blockMargin(stepId)}
    >
      {withLineBreaks(item?.text || '')}
    </Banner>
  );
};

/**
 * Renders tutorial content array
 * @param {Array} contentArray - Array of content items
 * @param {string} stepId - Current step ID
 * @returns {JSX.Element|null} - Rendered content or null
 */
export const renderContent = (contentArray, stepId) => {
  if (!contentArray || !Array.isArray(contentArray)) return null;

  return contentArray.map((item, index) => {
    // FIX: Add null check for item
    if (!item || typeof item !== 'object') return null;

    switch (item.type) {
      case 'paragraph':
        return renderParagraph(item, index, stepId);
      case 'list':
        return renderList(item, index, stepId);
      case 'important':
        return renderCallout(item, index, stepId, 'warning');
      case 'tip':
      case 'note':
      case 'highlight':
      case 'callout':
      case 'action':
        return renderCallout(item, index, stepId, 'info');
      case 'video':
        return renderVideo(item, index);
      case 'script':
        return renderScript(item, index, stepId);
      case 'example':
        return renderExample(item, index, stepId);
      case 'info':
        return renderInfo(item, index, stepId);
      default:
        return null;
    }
  });
};
